import React, { useState, useEffect, useMemo } from "react";
import { Modal, InputNumber, Tag, Spin, Badge, Slider } from "antd";
import {
  BenchmarkConfig,
  fetchBenchmarkInputFolders,
} from "@/lib/api/resources/benchmark";
import { useQuery } from "@tanstack/react-query";
import {
  RobotOutlined,
  FolderOpenOutlined,
  SafetyCertificateOutlined,
  CodeOutlined,
} from "@ant-design/icons";

interface RunSettingsModalProps {
  open: boolean;
  config: BenchmarkConfig | null;
  onClose: () => void;
  onRun: (config: BenchmarkConfig, overrideLimit: number | null) => void;
  isPending: boolean;
}

export const RunSettingsModal: React.FC<RunSettingsModalProps> = ({
  open,
  config,
  onClose,
  onRun,
  isPending,
}) => {
  const defaultLimit = config?.config?.limit ? Number(config.config.limit) : 1;
  const [limit, setLimit] = useState<number | null>(defaultLimit);

  useEffect(() => {
    if (open) {
      setLimit(defaultLimit);
    }
  }, [open, defaultLimit]);

  const { data: inputFolders, isLoading: isLoadingFolders } = useQuery({
    queryKey: ["benchmarkInputFolders"],
    queryFn: fetchBenchmarkInputFolders,
    enabled: open,
  });

  const folderName =
    config?.config?.input?.folder ||
    config?.config?.imageFolder ||
    config?.config?.emlFolder ||
    config?.config?.folder;

  const matchingFolder = useMemo(() => {
    const foldersArray = inputFolders?.["hydra:member"] || inputFolders || [];
    if (folderName && Array.isArray(foldersArray)) {
      for (const group of foldersArray) {
        if (group.folders && Array.isArray(group.folders)) {
          const found = group.folders.find(
            (f: any) => f.id === folderName || f.name === folderName,
          );
          if (found) return found;
        }
      }
    }
    return null;
  }, [folderName, inputFolders]);

  const maxCases = matchingFolder?.count;
  const judgeEnabled = config?.config?.judge?.enabled;
  const configType = config?.config?.type;

  return (
    <Modal
      title={<span className="text-lg font-bold">Run Benchmark</span>}
      open={open}
      onCancel={onClose}
      onOk={() => config && onRun(config, limit)}
      okText="Start Execution"
      okButtonProps={{
        className: "bg-orange-500 hover:bg-orange-600 text-white border-none",
        size: "large",
        shape: "round",
      }}
      cancelButtonProps={{ size: "large", shape: "round" }}
      confirmLoading={isPending}
      destroyOnHidden
      width={600}
    >
      <div className="py-2 space-y-6">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-5 shadow-inner">
          <h4 className="text-blue-900 font-semibold mb-3 flex items-center gap-2">
            <RobotOutlined /> Configuration Overview
          </h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 block mb-1 text-xs uppercase tracking-wider font-semibold">
                Config Name
              </span>
              <strong className="text-gray-800">{config?.name}</strong>
            </div>
            <div>
              <span className="text-gray-500 block mb-1 text-xs uppercase tracking-wider font-semibold">
                Capability
              </span>
              <Tag
                color="geekblue"
                className="rounded-md border-0 bg-blue-100 text-blue-700"
              >
                <CodeOutlined className="mr-1" />
                {configType || "Unknown"}
              </Tag>
            </div>
            <div>
              <span className="text-gray-500 block mb-1 text-xs uppercase tracking-wider font-semibold">
                Input Source
              </span>
              <div className="flex items-center gap-1.5 text-gray-800">
                <FolderOpenOutlined className="text-amber-500" />
                <span className="font-medium">{folderName || "None"}</span>
                {isLoadingFolders ? (
                  <Spin size="small" className="ml-2" />
                ) : maxCases !== undefined ? (
                  <Badge
                    count={maxCases}
                    style={{ backgroundColor: "#52c41a", marginLeft: 4 }}
                  />
                ) : null}
              </div>
            </div>
            <div>
              <span className="text-gray-500 block mb-1 text-xs uppercase tracking-wider font-semibold">
                LLM Judge
              </span>
              {judgeEnabled ? (
                <Tag
                  color="success"
                  className="rounded-md border-0 bg-green-100 text-green-700"
                >
                  <SafetyCertificateOutlined className="mr-1" /> Enabled
                </Tag>
              ) : (
                <Tag
                  color="default"
                  className="rounded-md border-0 bg-gray-100 text-gray-500"
                >
                  Disabled
                </Tag>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2">
            Test Case Limit
          </label>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-6 mb-4">
              <Slider
                className="flex-grow mt-2"
                min={1}
                max={maxCases || 100}
                value={limit || maxCases || 1}
                onChange={(v) => setLimit(v)}
                disabled={!maxCases}
                tooltip={{
                  formatter: (val) => (val === maxCases ? "All" : val),
                }}
                marks={{
                  1: <span className="text-gray-400 text-xs">1</span>,
                  [maxCases || 100]: (
                    <span className="text-gray-400 text-xs font-semibold">
                      {maxCases ? `${maxCases} (All)` : "100"}
                    </span>
                  ),
                }}
              />
              <InputNumber
                className="w-40 text-lg shadow-sm text-center font-mono"
                size="large"
                min={1}
                max={maxCases}
                precision={0}
                value={limit}
                onChange={(v) => setLimit(v ? Number(v) : null)}
                placeholder={maxCases ? `No limit (${maxCases})` : "No limit"}
              />
            </div>
            <div className="mt-3 text-sm text-gray-500 bg-white p-3 rounded border border-gray-100 leading-relaxed">
              {maxCases ? (
                <span>
                  The selected folder contains{" "}
                  <strong>{maxCases} test cases</strong>.
                  {limit && limit < maxCases
                    ? ` You are capping this run to ${limit} cases.`
                    : ` If left empty, all ${maxCases} cases will be executed.`}
                </span>
              ) : (
                <span>
                  Leave empty to run all available test cases in the folder.
                </span>
              )}
              <br />
              <span className="text-gray-400 text-xs mt-1 block">
                Limiting test cases is highly recommended when testing new
                prompts to manage LLM token costs.
              </span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
